import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "./header/Logo";
import Navigation from "./header/Navigation";
import UserActions from "./header/UserActions";
import MobileMenu from "./header/MobileMenu";
import LanguageSwitcher from "./header/LanguageSwitcher";
import { Menu, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/context/LanguageContext";
import { commonMenuLabels } from "@/i18n";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import MobileAppHeader from "./header/MobileAppHeader";

const Header = () => {
  const { isNative } = useNativePlatform();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const isDark = theme === "dark";
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const menuLabels = commonMenuLabels(language);

  if (isNative) {
    return <MobileAppHeader />;
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // When logged out: nav + theme + login go in menu (both mobile & desktop). Menu button is rightmost.
  const showMenuForLoggedOut = !user;
  const showNavInHeader = !isMobile && user;
  const isDesktopLoggedOut = !user && !isMobile;

  const handleMenuButtonMouseEnter = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Open menu on hover (desktop only when logged out)
    if (isDesktopLoggedOut && !isMenuOpen) {
      setIsMenuOpen(true);
    }
  };

  const handleMenuButtonMouseLeave = () => {
    // Delay closing to allow moving mouse to dropdown
    if (isDesktopLoggedOut) {
      closeTimeoutRef.current = setTimeout(() => {
        setIsMenuOpen(false);
      }, 100);
    }
  };

  const handleDropdownMouseEnter = () => {
    // Clear close timeout when mouse enters dropdown
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleDropdownMouseLeave = () => {
    // Close menu when mouse leaves dropdown
    if (isDesktopLoggedOut) {
      setIsMenuOpen(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <header
      className={`fixed top-4 left-1/2 -translate-x-1/2 w-[calc(100%-1rem)] sm:w-[calc(100%-1.5rem)] md:w-[95%] z-50 overflow-x-hidden rounded-3xl ${
        isDark
          ? "bg-black/85 border-b border-white/10"
          : "bg-white/95 border-b border-gray-200"
      }`}
    >
      <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-2">
        <div className="flex items-center justify-between h-14 sm:h-12 min-w-0">
          <div className="flex items-center space-x-4 sm:space-x-8 min-w-0 flex-1">
            <Logo />
            {showNavInHeader && <Navigation />}
          </div>

          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {!user && <LanguageSwitcher />}
            {!user && (
              <button
                type="button"
                className="hover:bg-accent/10 font-medium shrink-0 px-2.5 py-2 sm:px-2 sm:py-1.5 rounded-md text-base sm:text-sm whitespace-nowrap transition-colors"
                onClick={() => navigate("/auth")}
              >
                {menuLabels.login}
              </button>
            )}
            <UserActions />
            {(isMobile || showMenuForLoggedOut) && (
              <button
                ref={menuButtonRef}
                onClick={() => {
                  if (isMobile) toggleMenu();
                  else setIsMenuOpen((v) => !v);
                }}
                onMouseEnter={handleMenuButtonMouseEnter}
                onMouseLeave={handleMenuButtonMouseLeave}
                className="p-2.5 sm:p-2 flex-shrink-0 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 sm:w-[18px] sm:h-[18px]" />
                ) : (
                  <Menu className="w-6 h-6 sm:w-[18px] sm:h-[18px]" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <MobileMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        menuButtonRef={menuButtonRef}
        isDesktopLoggedOut={isDesktopLoggedOut}
        onDropdownMouseEnter={handleDropdownMouseEnter}
        onDropdownMouseLeave={handleDropdownMouseLeave}
      />
    </header>
  );
};

export default Header;
